import type { ItemProgress } from './elapsed'

/**
 * A cook's tap, read off the line - `FRONTEND.md` §9.811, BACKEND.md §10A.454, owner O157: *"vykliknutie nech sa
 * zobrazuje aj na ostatných zariadeniach a nech po refreshi zostane vykliknuté"*.
 *
 * The mark lives on the order line (`kitchenProgress` on `GET /orders`), so every tablet draws the same thing and a
 * refresh keeps it. A tap is drawn at once and held here, by the line's row id, until the server has it and the
 * board has re-read the orders; then the server's mark is the only one.
 */

/** The server's mark. Anything but its two marks is an untouched line, so an unknown value never draws as made. */
export function serverProgressOf(line: { kitchenProgress?: unknown }): ItemProgress {
  const value = line.kitchenProgress
  return value === 'working' || value === 'done' ? value : 'idle'
}

/** What the board draws: a tap the server has not confirmed yet wins over the server's mark. */
export function shownProgress(
  line: { id?: number; kitchenProgress?: unknown },
  pending: Readonly<Record<number, ItemProgress>>,
): ItemProgress {
  if (typeof line.id === 'number') {
    const tapped = pending[line.id]
    if (tapped !== undefined) return tapped
  }
  return serverProgressOf(line)
}

/**
 * The pending taps without this one, once its write has settled and the orders are re-read.
 *
 * **Only if it is still the tap that settled.** A cook tapping twice quickly (*being made*, then *made*) has two
 * writes in flight; the first settling must not drop the second, or the line flickers back to *being made*. Returns
 * the same object when nothing is dropped, so a caller can tell a no-op from a change.
 */
export function settledTap(
  pending: Readonly<Record<number, ItemProgress>>,
  lineId: number,
  tapped: ItemProgress,
): Readonly<Record<number, ItemProgress>> {
  if (pending[lineId] !== tapped) return pending
  const rest = { ...pending }
  delete rest[lineId]
  return rest
}
